import { For, Show, onMount, createSignal } from "solid-js";
import autoAnimate from "@formkit/auto-animate";
import { PanelHeader } from "./PanelHeader";
import toast from "solid-toast";
import { uiHash, uiLog } from "../utils/telemetry";

interface QAItem {
  id?: string;
  question: string;
  answer?: string;
  answer_en?: string;
  paraphrase?: string;
  source?: "manual" | "ai" | "posthoc";
  origin?: "template" | "context" | "adhoc";
  method?: "auto" | "backfill" | "slot" | "pii";
  answerStatus?: "extracting" | "answered" | "inferred" | "no_answer";
  confidence?: number;
  askedAt?: string | number | Date;
  answeredAt?: string | number | Date;
  quote?: string;
  priority_level?: "Critical" | "Important" | "Informational";
}

export default function AnsweredPanel(props: {
  answered: QAItem[];
  onUpdate?: (
    originalQuestion: string,
    changes: Partial<Pick<QAItem, "question" | "answer">>,
  ) => void;
  onRemove?: (question: string) => void;
  onAskDrop?: (
    payload:
      | {
          question: string;
          priority_level?: "Critical" | "Important" | "Informational";
          fieldKey?: string | null;
          origin?: "template" | "context" | "adhoc";
          id?: string;
        }
      | string,
  ) => void;
  onConfirmInferred?: (question: string) => void;
  onReturn?: (question: string) => void;
  readOnly?: boolean;
}) {
  const [editingIndex, setEditingIndex] = createSignal<number | null>(null);
  const [editQ, setEditQ] = createSignal("");
  const [editA, setEditA] = createSignal("");
  const [showTagsHelp, setShowTagsHelp] = createSignal(false);
  let listRef: HTMLUListElement | undefined;

  function startEdit(i: number, qa: QAItem) {
    setEditingIndex(i);
    setEditQ(qa.question);
    setEditA(qa.answer || "");
  }
  function cancelEdit() {
    setEditingIndex(null);
    setEditQ("");
    setEditA("");
  }
  function saveEdit(originalQuestion: string) {
    props.onUpdate?.(originalQuestion, { question: editQ(), answer: editA() });
    cancelEdit();
  }
  onMount(() => {
    listRef && autoAnimate(listRef);
  });

  function handleDrop(e: DragEvent) {
    // Always let native text drops go through unless our custom payload is present
    const payloadStr = e.dataTransfer?.getData("application/x-question");
    if (!payloadStr) return; // allow normal text drop

    e.preventDefault();
    try {
      const payload = JSON.parse(payloadStr);
      const qText =
        typeof payload === "string" ? payload : payload.question || "";

      uiLog("asked_manual_drop", {
        question_sha: uiHash(qText),
        priority_level:
          typeof payload === "string" ? undefined : payload.priority_level,
      });

      // Hand off to parent. If parent ignores, show a helpful toast.
      const before = (props as any).answered?.length || 0;
      props.onAskDrop?.(payload as any);
      const after = (props as any).answered?.length || 0;
      if (after === before) {
        toast("Already asked or answered.");
      }
    } catch (err) {
      console.warn("AnsweredPanel.handleDrop parse error:", err);
      toast.error("Could not add the question. Invalid drag payload.");
    }
  }

  return (
    <section
      class="card"
      onDragOver={(e) => {
        if (!props.readOnly) e.preventDefault();
      }}
      onDrop={(e) => {
        if (props.readOnly) return; // hard block in read-only
        handleDrop(e);
      }}
    >
      <div class="flex items-center gap-2 mb-0.5">
        <span>✅</span>
        <PanelHeader title="Answered Questions" />
        {/* Help icon for tag explanations */}
        <button
          type="button"
          class="ml-auto text-gray-400 hover:text-gray-600 transition"
          title="What do the tags mean?"
          onClick={() => setShowTagsHelp(!showTagsHelp())}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>
      </div>

      {/* Tags help tooltip */}
      <Show when={showTagsHelp()}>
        <div class="mb-3 p-3 bg-slate-50 border rounded-lg text-xs space-y-2">
          <div class="font-semibold text-slate-700 mb-2">Tag Meanings:</div>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <span class="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 mr-1">
                Template
              </span>
              <span class="text-slate-600">
                Question from interview template
              </span>
            </div>
            <div>
              <span class="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 mr-1">
                Context
              </span>
              <span class="text-slate-600">
                AI-generated Qs based on conversation
              </span>
            </div>
            <div>
              <span class="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 mr-1">
                Adhoc
              </span>
              <span class="text-slate-600">
                Detected from transcript post-call
              </span>
            </div>
            <div>
              <span class="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 mr-1">
                AI
              </span>
              <span class="text-slate-600">Auto-moved by AI detection</span>
            </div>
            <div>
              <span class="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 mr-1">
                Critical/Important/Info
              </span>
              <span class="text-slate-600">Priority level</span>
            </div>
            <div>
              <span class="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mr-1">
                Needs Review
              </span>
              <span class="text-slate-600">Low confidence, verify answer</span>
            </div>
            <div>
              <span class="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mr-1">
                Extracting
              </span>
              <span class="text-slate-600">AI is finding the answer</span>
            </div>
            <div>
              <span class="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mr-1">
                No Answer
              </span>
              <span class="text-slate-600">
                Question asked but not answered
              </span>
            </div>
          </div>
          <button
            class="mt-2 text-slate-500 hover:text-slate-700 underline"
            onClick={() => setShowTagsHelp(false)}
          >
            Close
          </button>
        </div>
      </Show>

      {props.answered.length === 0 ? (
        <p class="text-gray-500 text-sm mt-1">Nothing yet.</p>
      ) : (
        <ul class="space-y-2" ref={(el) => (listRef = el as HTMLUListElement)}>
          <For each={props.answered}>
            {(qa, i) => {
              const isEditing = () => editingIndex() === i();
              const displayAnswer = () =>
                qa.answer_en || qa.paraphrase || qa.answer || "";
              const statusLabel = () => {
                switch (qa.answerStatus) {
                  case "inferred":
                    return "Needs review";
                  case "extracting":
                    return "Extracting";
                  case "no_answer":
                    return "No clear answer";
                  case "answered":
                  default:
                    return "";
                }
              };
              return (
                <li
                  class="rounded border p-2 bg-white transition"
                  draggable={!isEditing()}
                  onDragStart={(e) => {
                    try {
                      e.dataTransfer?.setData(
                        "application/x-question",
                        JSON.stringify({
                          question: qa.question,
                          priority_level: qa.priority_level,
                          from: "answered",
                        }),
                      );
                      console.log(
                        "drag drom answered:",
                        qa.question,
                        qa.priority_level,
                      );
                    } catch {}
                  }}
                >
                  <Show
                    when={!isEditing()}
                    fallback={
                      <div class="flex flex-col gap-1">
                        <input
                          class="border rounded px-2 py-1 text-sm"
                          value={editQ()}
                          onInput={(e) => setEditQ(e.currentTarget.value)}
                        />
                        <textarea
                          class="border rounded px-2 py-1 text-sm"
                          rows={2}
                          value={editA()}
                          onInput={(e) => setEditA(e.currentTarget.value)}
                        />
                        <div class="flex gap-2 justify-end">
                          <button
                            class="px-2 py-1 text-sm"
                            onClick={cancelEdit}
                          >
                            Cancel
                          </button>
                          <button
                            class="px-3 py-1 text-sm rounded bg-blue-600 text-white"
                            onClick={() => saveEdit(qa.question)}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    }
                  >
                    <div class="flex items-start justify-between gap-2">
                      <div>
                        <div class="font-medium text-[14px]">{qa.question}</div>
                        <div class="text-sm text-gray-700">
                          {displayAnswer() ? (
                            displayAnswer()
                          ) : (
                            <span class="italic text-gray-400">
                              {qa.answerStatus === "no_answer"
                                ? "No clear answer"
                                : "…waiting…"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        {/* Origin badge: where the question came from */}
                        <Show when={qa.origin}>
                          <span
                            class={`px-1.5 py-0.5 text-[10px] rounded ${
                              qa.origin === "template"
                                ? "bg-blue-100 text-blue-700"
                                : qa.origin === "context"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-gray-100 text-gray-700"
                            }`}
                            title={`Question from ${qa.origin}`}
                          >
                            {qa.origin === "template"
                              ? "Template"
                              : qa.origin === "context"
                                ? "Context"
                                : "Adhoc"}
                          </span>
                        </Show>
                        {/* Source badge: how it was marked */}
                        {qa.source === "ai" && (
                          <span
                            class="px-1.5 py-0.5 text-[10px] rounded bg-indigo-100 text-indigo-700"
                            title="Auto-assessed by AI"
                          >
                            AI
                          </span>
                        )}
                        <Show when={qa.priority_level}>
                          <span class="px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-700">
                            {qa.priority_level}
                          </span>
                        </Show>
                        <Show when={statusLabel()}>
                          <span
                            class="px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800"
                            title="Answer status"
                          >
                            {statusLabel()}
                          </span>
                        </Show>
                        <Show
                          when={
                            qa.answerStatus === "inferred" && !props.readOnly
                          }
                        >
                          <button
                            class="px-2 py-1 text-sm rounded bg-emerald-100 text-emerald-800"
                            onClick={() =>
                              props.onConfirmInferred?.(qa.question)
                            }
                          >
                            Confirm
                          </button>
                        </Show>
                        {/*    <Show when={props.onReturn && qa.source === "ai"}>
                          <button
                            class="px-2 py-1 text-sm rounded bg-yellow-100"
                            title="Move back to Suggestions"
                            onClick={() => {
                              console.log(
                                "↩️ Return to Suggestions clicked for:",
                                qa.question
                              ); // DEBUG
                              props.onReturn?.(qa.question);
                            }}
                          >
                            Return
                          </button>
                        </Show> */}
                        <button
                          class="px-2 py-1 text-sm rounded bg-gray-100"
                          onClick={() => startEdit(i(), qa)}
                        >
                          Edit
                        </button>
                        <Show when={props.onRemove}>
                          <button
                            class="px-2 py-1 text-sm rounded bg-rose-100"
                            onClick={() => props.onRemove?.(qa.question)}
                          >
                            Delete
                          </button>
                        </Show>
                      </div>
                    </div>
                  </Show>
                </li>
              );
            }}
          </For>
        </ul>
      )}
    </section>
  );
}
